// ============================================================================
// SECOND SCREEN PAGE - Chromeless fullscreen mission control display
// Rendered in a popup window, receives data via BroadcastChannel
// No nav bars, headers, or sidebars — pure display surface
// ============================================================================

import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SecondScreenDataProvider, useSecondScreenData } from '../contexts/SecondScreenContext';
import SecondScreenGrid from '../components/second-screen/SecondScreenGrid';
import ConnectionStatus from '../components/second-screen/ConnectionStatus';
import SpotlightOverlay from '../components/second-screen/SpotlightOverlay';
import MindBubbleOverlay from '../components/second-screen/MindBubbleOverlay';
import CinematicCardOverlay from '../components/second-screen/CinematicCardOverlay';
import CinematicContributionOverlay from '../components/second-screen/CinematicContributionOverlay';
import TaskCardOverlay from '../components/second-screen/TaskCardOverlay';
import BreakingNewsOverlay from '../components/second-screen/BreakingNewsOverlay';
import AiActivityPulseOverlay from '../components/second-screen/AiActivityPulseOverlay';
import HoverToolbar from '../components/second-screen/HoverToolbar';

const LAYOUT_KEY_PREFIX = 'second-screen-layout-';

const SecondScreenContent: React.FC<{ sessionId: string; screenNumber: number }> = ({ sessionId, screenNumber }) => {
  const { theme, overscan } = useSecondScreenData();
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(true);
  // Track the active layout ID so cinematic overlays are only shown on 'cinematic-live'
  const [activeLayoutId, setActiveLayoutId] = useState<string>(() => {
    try {
      const raw = localStorage.getItem(`${LAYOUT_KEY_PREFIX}${sessionId}-screen${screenNumber}`);
      return raw ? (JSON.parse(raw)?.id ?? '') : '';
    } catch { return ''; }
  });

  useEffect(() => {
    const key = `${LAYOUT_KEY_PREFIX}${sessionId}-screen${screenNumber}`;
    const handleStorage = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        try { setActiveLayoutId(JSON.parse(e.newValue)?.id ?? ''); } catch { /* ignore */ }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [sessionId, screenNumber]);

  const isCinematic = activeLayoutId === 'cinematic-live';

  const enterFullscreen = useCallback(() => {
    setShowFullscreenPrompt(false);
    document.documentElement.requestFullscreen?.().catch(() => {
      // Fullscreen request denied — just dismiss the prompt
    });
  }, []);

  const dismissPrompt = useCallback(() => {
    setShowFullscreenPrompt(false);
  }, []);

  return (
    <div className={`w-screen h-screen overflow-hidden relative ${
      theme === 'dark' ? 'bg-black text-white' : 'bg-slate-100 text-slate-900'
    }`}>
      {/* Safe area: inset from all four edges so right/bottom are honoured */}
      <div style={{
        position: 'absolute',
        top: overscan.top,
        right: overscan.right,
        bottom: overscan.bottom,
        left: overscan.left,
        overflow: 'hidden',
      }}>
      <div style={{ position: 'absolute', inset: 0 }} className="relative">
      <ConnectionStatus />
      <SecondScreenGrid sessionId={sessionId} screenNumber={screenNumber} />
      <SpotlightOverlay />
      <BreakingNewsOverlay />
      {isCinematic && <MindBubbleOverlay />}
      {isCinematic && <CinematicCardOverlay />}
      {isCinematic && <CinematicContributionOverlay />}
      {isCinematic && <TaskCardOverlay />}
      <AiActivityPulseOverlay />
      <HoverToolbar sessionId={sessionId} screenNumber={screenNumber} />

      </div>
      </div>{/* end safe area */}
      {/* Fullscreen prompt overlay */}
      {showFullscreenPrompt && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center gap-6 max-w-md text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Mission Control</h2>
              <p className="text-sm text-white/60">Drag this window to your external display, then go fullscreen for the best experience.</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={enterFullscreen}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-dark transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                </svg>
                Enter Fullscreen
              </button>
              <button
                onClick={dismissPrompt}
                className="px-4 py-3 rounded-xl text-white/50 text-sm font-medium hover:text-white/80 transition-colors"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SecondScreenPage: React.FC = () => {
  const { id: sessionId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const screenNumber = parseInt(searchParams.get('screen') || '1', 10) || 1;

  if (!sessionId) {
    return (
      <div className="w-screen h-screen bg-navy flex items-center justify-center text-white">
        <p className="text-lg">No session ID provided</p>
      </div>
    );
  }

  return (
    <SecondScreenDataProvider sessionId={sessionId} screenNumber={screenNumber}>
      <SecondScreenContent sessionId={sessionId} screenNumber={screenNumber} />
    </SecondScreenDataProvider>
  );
};

export default SecondScreenPage;
